class UiDomUtils {
    static show($el) {
        $el.removeAttribute('hidden');
    }
    static hide($el) {
        $el.setAttribute('hidden', '');
    }
    static empty($el) {
        $el.innerHTML = '';
    }
    static remove($el) {
        $el.remove();
    }
    static makeUnnavigable($el) {
        $el.setAttribute('tabindex', '-1');
    }
    static makeNavigable($el) {
        $el.setAttribute('tabindex', '0');
    }
    static getFocusableSelector() {
        return ('' +
            // Namespace problems of [xlink:href] explained in https://stackoverflow.com/a/23047888/515124
            // svg a[*|href] does not match in IE9, but since we're filtering
            // through is/focusable we can include all <a> from SVG
            'svg a,' +
            // may behave as 'svg, svg *,' in chrome as *every* svg element with a focus event listener is focusable
            // navigational elements
            'a[href],' +
            // validity determined by is/valid-area.js
            'area[href],' +
            // validity determined by is/disabled.js
            'input:not([type="hidden"]), select, textarea, button,' +
            // browsing context containers
            'iframe, object, embed,' +
            // interactive content
            'keygen,' +
            // validity determined by is/valid-tabindex.js
            '[tabindex]:not([tabindex="-1"]),' +
            // editing hosts
            '[contenteditable]');
    }
    static getFocusableElements($container) {
        return [...$container.querySelectorAll(this.getFocusableSelector())].filter(($el) => !this._isNotFocusable($el));
    }
    static getFocusableElement($container) {
        return [...$container.querySelectorAll(this.getFocusableSelector())].filter(($el) => !this._isNotFocusable($el))[0];
    }
    static getLastFocusableElement($container) {
        const focusableElements = this.getFocusableElements($container);
        return focusableElements[focusableElements.length - 1];
    }
    static _isNotFocusable($el) {
        const displayValue = getComputedStyle($el).getPropertyValue('display');
        const isDisabled = $el.hasAttribute('disabled') || !!$el.closest('[disabled]');
        const isInerted = $el.hasAttribute('inert') || !!$el.closest('[inert]');
        const isHidden = $el.hasAttribute('hidden') || !!$el.closest('[hidden]');
        const isAriaHidden = $el.getAttribute('aria-hidden') === 'true' || !!$el.closest('[aria-hidden="true"]');
        const hasHiddenParent = !!this._findHiddenParent($el);
        return !!(isDisabled ||
            isAriaHidden ||
            isHidden ||
            displayValue === 'none' ||
            isInerted ||
            hasHiddenParent);
    }
    static _findHiddenParent($el) {
        let $parent = $el.parentElement;
        while ($parent) {
            const displayValue = getComputedStyle($parent).getPropertyValue('display');
            if (displayValue === 'none')
                return parent;
            $parent = ($parent === null || $parent === void 0 ? void 0 : $parent.parentElement) || null;
        }
        return null;
    }
    static isFocusable($element) {
        return $element.matches(this.getFocusableSelector());
    }
    static setFocusToFirstFocusableElementInContainer($container) {
        var _a;
        (_a = this.getFocusableElement($container)) === null || _a === void 0 ? void 0 : _a.focus();
    }
    static keepFocusWithinContainer($container, ev) {
        const isTabPressed = ev.code === 'Tab';
        const firstFocusableElement = UiDomUtils.getFocusableElement($container);
        const lastFocusableElement = UiDomUtils.getLastFocusableElement($container);
        if (isTabPressed) {
            if (ev.shiftKey) {
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus();
                    ev.preventDefault();
                }
            }
            else if (document.activeElement === lastFocusableElement) {
                firstFocusableElement.focus();
                ev.preventDefault();
            }
        }
    }
    static getDirectChildren($el, selector) {
        return selector ? [...$el.children].filter((child) => child.matches(selector)) : [...$el.children];
    }
    static getAllClosest($el, selector, elements = []) {
        if (!$el)
            return elements;
        const $closestEl = $el.closest(selector);
        if ($closestEl)
            elements.push($closestEl);
        if (!$closestEl)
            return elements;
        return UiDomUtils.getAllClosest($closestEl.parentElement, selector, elements);
    }
    static getFirstParentNonStaticElement($el) {
        let $currentEl = $el.parentElement;
        while ($currentEl) {
            if ($currentEl && window.getComputedStyle($currentEl).position !== 'static') {
                return $currentEl;
            }
            $currentEl = $currentEl.parentElement;
        }
        return null;
    }
    static getFirstScrollableParent($el) {
        let currentParent = $el.parentElement;
        while (currentParent) {
            if (currentParent.scrollHeight > currentParent.clientHeight) {
                return currentParent;
            }
            currentParent = currentParent === null || currentParent === void 0 ? void 0 : currentParent.parentElement;
        }
        return null;
    }
    static getElementNamesValuesAttributes($el) {
        return Array.from($el.attributes).reduce((acc, attr) => ({
            ...acc,
            [attr.name]: attr.value
        }), {});
    }
    static getPreviousSibling(elem, selector) {
        if (!elem)
            return;
        let sibling = elem.previousElementSibling;
        if (!selector)
            return sibling;
        while (sibling) {
            if (sibling.matches(selector))
                return sibling;
            sibling = sibling.previousElementSibling;
        }
        return sibling;
    }
    static getNextSibling(elem, selector) {
        if (!elem)
            return;
        let sibling = elem.nextElementSibling;
        if (!selector)
            return sibling;
        while (sibling) {
            if (sibling.matches(selector))
                return sibling;
            sibling = sibling.nextElementSibling;
        }
        return sibling;
    }
    static getNextFocusableElement($element) {
        var _a;
        const focusableElements = UiDomUtils.getFocusableElements(document.body);
        const nextIndex = focusableElements.indexOf($element) + 1;
        return (_a = focusableElements[nextIndex]) !== null && _a !== void 0 ? _a : focusableElements[0];
    }
    static getPreviousFocusableElement($element) {
        var _a;
        const focusableElements = UiDomUtils.getFocusableElements(document.body);
        const previousIndex = focusableElements.indexOf($element) - 1;
        return (_a = focusableElements[previousIndex]) !== null && _a !== void 0 ? _a : focusableElements[focusableElements.length - 1];
    }
    static isElementEmpty($el) {
        return !$el || !$el.children || $el.children.length === 0;
    }
}
UiDomUtils.scrollIntoViewWithOffset = ($el, offset = 0, settings = {
    window: globalThis,
    document: globalThis.document.body,
    behavior: 'smooth'
}) => {
    const { window, document, behavior } = settings;
    window.scrollTo({
        behavior,
        top: $el.getBoundingClientRect().top - document.getBoundingClientRect().top - offset
    });
};

export { UiDomUtils };
//# sourceMappingURL=ui_dom_utils.js.map
